import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ClipboardCheck, Loader2, Save } from 'lucide-react';

export const QCTemplateDialog = ({ open, onClose, onSave, editing, form, setForm, services, saving }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md" data-testid="template-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-[#F56F05]" />
          {editing ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label className="text-xs font-bold text-zinc-500">Template Name *</Label>
          <Input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Deep Reset Checklist"
            data-testid="tpl-name-input"
          />
        </div>
        <div>
          <Label className="text-xs font-bold text-zinc-500">Assign to Service</Label>
          <Select
            value={form.applies_to_service_id || 'none'}
            onValueChange={v => setForm(f => ({ ...f, applies_to_service_id: v === 'none' ? '' : v }))}
          >
            <SelectTrigger data-testid="tpl-service-select"><SelectValue placeholder="All services (default)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All services (default fallback)</SelectItem>
              {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-zinc-400 mt-1">Service-specific templates override the default.</p>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Default Template</Label>
            <p className="text-[10px] text-zinc-400">Used when no service-specific template exists</p>
          </div>
          <Switch
            checked={form.is_default}
            onCheckedChange={v => setForm(f => ({ ...f, is_default: v }))}
            data-testid="tpl-default-switch"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-[#F56F05] hover:bg-[#C2410C] text-white"
            data-testid="save-template-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            {editing ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const QCItemDialog = ({ open, onClose, onSave, editing, form, setForm, saving }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md" data-testid="item-dialog">
      <DialogHeader>
        <DialogTitle>{editing ? 'Edit Item' : 'Add Checklist Item'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label className="text-xs font-bold text-zinc-500">Item Label *</Label>
          <Input
            value={form.label}
            onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            placeholder="e.g. All surfaces dusted and wiped"
            data-testid="item-label-input"
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Required</Label>
            <p className="text-[10px] text-zinc-400">Cleaner must complete before finishing</p>
          </div>
          <Switch
            checked={form.is_required}
            onCheckedChange={v => setForm(f => ({ ...f, is_required: v }))}
            data-testid="item-required-switch"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-[#F56F05] hover:bg-[#C2410C] text-white"
            data-testid="save-item-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            {editing ? 'Update' : 'Add'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const QCDeleteDialog = ({ open, onClose, onConfirm, type, target }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-sm" data-testid="delete-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-600">
          Delete {type === 'template' ? 'Template' : 'Item'}?
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm text-zinc-600 py-2">
        {type === 'template'
          ? `This will permanently delete "${target?.name}" and all its checklist items. Existing appointment checklists won't be affected.`
          : `This will permanently remove "${target?.label}" from the template.`}
      </p>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={onConfirm}
          data-testid="confirm-delete-btn"
        >
          Delete
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
